import moment from 'moment';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import Input from 'components/FormFields/Input';
import Radio from 'components/FormFields/Radio';
import Select from 'components/FormFields/Select';
import Textarea from 'components/FormFields/Textarea';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  FormWrapper,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalContent,
  ModalFooter,
  PrimaryButton,
  SecondaryButton,
} from 'components/FormModal';
import { useMyOwnershipQuery } from 'components/Ownership/gql/me';
import PhoneInput from 'components/PhoneInput';
import { useCreateGuest } from 'pages/track/Guests/gql/useCreateGuest';
import { useGetGuestAndUserNotificationEvents } from 'pages/track/Guests/gql/useGetGuest';
import { useUpdateGuest } from 'pages/track/Guests/gql/useUpdateGuest';

// Close icon SVG
const CloseIcon = () => (
  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
  </svg>
);

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const InviteMessage = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  margin: 8px 0 0 0;
  padding: 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(250, 70, 22, 0.1)'
      : 'rgba(250, 70, 22, 0.05)'};
  border-radius: 8px;
`;

// Helper to check if user has more than one venue
const hasMoreThanOneVenue = (user) => {
  if (!user?.ownership?.tracks?.length && !user?.ownership?.series?.length)
    return false;

  if (user.ownership.tracks.length + user.ownership.series.length < 2)
    return false;

  return true;
};

// Helper to format phone number for API
const formatPhoneForApi = (phone, countryCode) => {
  // Remove the country code prefix and any non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  const codeDigits = countryCode.replace('+', '');
  if (cleaned.startsWith(codeDigits)) {
    return cleaned.slice(codeDigits.length);
  }
  return cleaned;
};

// Helper to get calling code from country code
const getCallingCode = (countryCode) => {
  return countryCode.replace('+', '');
};

// Helper to get country code from calling code
const getCountryCodeFromCalling = (callingCode) => {
  const mapping = {
    1: 'US',
    44: 'GB',
    61: 'AU',
    86: 'CN',
    33: 'FR',
    49: 'DE',
    91: 'IN',
    81: 'JP',
    52: 'MX',
    234: 'NG',
    7: 'RU',
    82: 'KR',
    34: 'ES',
    971: 'AE',
  };
  return mapping[callingCode] || 'US';
};

const GuestFormModal = ({
  isOpen,
  onClose,
  guestId = null,
  onGuestCreated,
  onSuccess,
  onError,
  queryString = '',
}) => {
  const [modalRoot, setModalRoot] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+1');
  const [guestType, setGuestType] = useState('');
  const [year, setYear] = useState(moment().format('YYYY'));
  const [eventId, setEventId] = useState('');
  const [additionalGuests, setAdditionalGuests] = useState('');
  const [notes, setNotes] = useState('');
  const [ownership, setOwnership] = useState('');

  // Get data
  const { data } = useGetGuestAndUserNotificationEvents(guestId);
  const { data: myOwnershipData } = useMyOwnershipQuery();

  // Mutations
  const createGuest = useCreateGuest({ queryString });
  const updateGuest = useUpdateGuest();

  const guest = data?.getGuest;
  const events = data?.getUserEvents?.results ?? [];

  // Generate year options
  const thisYear = Number(moment().format('YYYY'));
  const yearOptions = [];
  for (let i = thisYear + 1; i >= thisYear - 5; i--) {
    yearOptions.push({ value: String(i), label: String(i) });
  }

  // Generate event options
  const eventOptions = events.map((event) => ({
    value: String(event.id),
    label: event.isMultiDay
      ? `${event.month.toUpperCase()} ${event.listDates} - ${event.name}`
      : `${event.day} ${event.date} - ${event.name}`,
  }));

  // Generate ownership options
  const ownershipOptions = [];
  if (myOwnershipData?.me?.ownership?.tracks) {
    myOwnershipData.me.ownership.tracks.forEach((track) => {
      ownershipOptions.push({ value: String(track.id), label: track.name });
    });
  }
  if (myOwnershipData?.me?.ownership?.series) {
    myOwnershipData.me.ownership.series.forEach((series) => {
      ownershipOptions.push({ value: String(series.id), label: series.name });
    });
  }

  const showOwnershipField =
    guestType === 'yearly_guests' && hasMoreThanOneVenue(myOwnershipData?.me);

  // Get modal root on mount
  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  // Reset form when modal opens/closes or guest changes
  useEffect(() => {
    if (isOpen) {
      if (guestId && guest) {
        // Edit mode - populate with guest data
        setFirstName(guest.first_name || '');
        setLastName(guest.last_name || '');
        setPhoneNumber(guest.phone_number || '');
        setCountryCode(`+${guest.calling_code || '1'}`);
        setGuestType(
          guest.year ? 'yearly_guests' : guest.event_id ? 'event_guests' : ''
        );
        setYear(guest.year ? String(guest.year) : moment().format('YYYY'));
        setEventId(guest.event_id ? String(guest.event_id) : '');
        setAdditionalGuests(
          guest.additional_guests ? String(guest.additional_guests) : ''
        );
        setNotes(guest.notes || '');
        setOwnership(
          guest.ownership?.value ? String(guest.ownership.value) : ''
        );
      } else {
        // Create mode - reset form
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        setCountryCode('+1');
        setGuestType('');
        setYear(moment().format('YYYY'));
        setEventId('');
        setAdditionalGuests('');
        setNotes('');
        setOwnership('');
      }
      setErrors({});
    }
  }, [isOpen, guestId, guest]);

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSubmitting, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!guestType) {
      newErrors.guestType = 'Please select a guest type';
    }

    if (guestType === 'yearly_guests' && !year) {
      newErrors.year = 'Please select a year';
    }

    if (guestType === 'event_guests' && !eventId) {
      newErrors.eventId = 'Please select an event';
    }

    if (showOwnershipField && !ownership) {
      newErrors.ownership = 'Please select a track or series';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const ownedTrackOrSeries =
        myOwnershipData?.me?.ownership?.tracks?.[0]?.id ??
        myOwnershipData?.me?.ownership?.series?.[0]?.id;

      const input = {
        ...(guestId ? { id: Number(guestId) } : null),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        phone_number: formatPhoneForApi(phoneNumber, countryCode),
        country_code: getCountryCodeFromCalling(getCallingCode(countryCode)),
        calling_code: getCallingCode(countryCode),
        year: guestType === 'yearly_guests' ? Number(year) : null,
        event_id: guestType === 'event_guests' ? Number(eventId) : null,
        notes: notes.trim() || null,
        additional_guests: additionalGuests ? Number(additionalGuests) : 0,
        ownership: ownership ? Number(ownership) : ownedTrackOrSeries || null,
      };

      const mutation = guestId ? updateGuest : createGuest;
      const response = await mutation(input);

      if (!response || response.errors) {
        const errorMsg = guestId
          ? 'Error updating guest'
          : 'Error creating guest';
        if (onError) {
          onError(errorMsg);
        } else {
          toast.error(errorMsg);
        }
        return;
      }

      const successMsg = guestId
        ? 'Guest updated successfully'
        : 'Guest added successfully';
      if (onSuccess) {
        onSuccess(successMsg);
      } else {
        toast.success(successMsg);
      }

      if (!guestId && onGuestCreated && response.data?.createGuestV2) {
        onGuestCreated(response.data.createGuestV2);
      }

      onClose();
    } catch (error) {
      const errorMsg = guestId
        ? 'Error updating guest'
        : 'Error creating guest';
      if (onError) {
        onError(errorMsg);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !modalRoot) return null;

  // Don't render if editing but guest data hasn't loaded yet
  if (guestId && !guest) return null;

  return ReactDOM.createPortal(
    <EnhancedThemeProvider>
      <Backdrop onClick={handleBackdropClick}>
        <ModalContainer
          role="dialog"
          aria-labelledby="guest-form-modal-title"
          aria-modal="true"
          maxWidth="600px"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton
            onClick={onClose}
            aria-label="Close modal"
            disabled={isSubmitting}
          >
            <CloseIcon />
          </CloseButton>

          <FormWrapper onSubmit={handleSubmit}>
            <ModalHeader>
              <ModalTitle id="guest-form-modal-title">
                {guestId ? 'Edit Guest' : 'Add New Guest'}
              </ModalTitle>
              <ModalSubtitle>
                {guestId
                  ? 'Update the guest information below'
                  : 'Fill out the form to add a new guest to your list'}
              </ModalSubtitle>
            </ModalHeader>

            <ModalContent>
              <FormSection>
                <FormRow>
                  <Input
                    label="First Name"
                    required
                    placeholder="Enter first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    error={!!errors.firstName}
                    helperText={errors.firstName}
                  />
                  <Input
                    label="Last Name"
                    required
                    placeholder="Enter last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    error={!!errors.lastName}
                    helperText={errors.lastName}
                  />
                </FormRow>

                <PhoneInput
                  label="Mobile Phone Number"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  countryCode={countryCode}
                  onCountryChange={setCountryCode}
                />

                <Radio
                  name="guestType"
                  label="Guest Type"
                  value={guestType}
                  onChange={(e) => setGuestType(e.target.value)}
                  options={[
                    { value: 'yearly_guests', label: 'Full Season Guest' },
                    { value: 'event_guests', label: 'Single Event Guest' },
                  ]}
                  inline
                  error={!!errors.guestType}
                  helperText={errors.guestType}
                />

                {guestType === 'yearly_guests' && (
                  <Select
                    label="Year"
                    required
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    options={yearOptions}
                    error={!!errors.year}
                    helperText={errors.year}
                  />
                )}

                {guestType === 'event_guests' && (
                  <Select
                    label="Event"
                    required
                    placeholder="Select an event"
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    options={eventOptions}
                    error={!!errors.eventId}
                    helperText={errors.eventId}
                  />
                )}

                {showOwnershipField && (
                  <Select
                    label="Track or Series"
                    required
                    placeholder="Select a track or series"
                    value={ownership}
                    onChange={(e) => setOwnership(e.target.value)}
                    options={ownershipOptions}
                    error={!!errors.ownership}
                    helperText={errors.ownership}
                  />
                )}

                <Input
                  label="Additional Guests Allowed"
                  placeholder="Number of additional guests"
                  type="number"
                  value={additionalGuests}
                  onChange={(e) => setAdditionalGuests(e.target.value)}
                />

                <Textarea
                  label="Internal Notes"
                  placeholder="Add any notes about this guest"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />

                {!guestId && phoneNumber && (
                  <InviteMessage>
                    The guest will receive a text message notifying them that
                    they are on the guest list and how many additional guests
                    they may bring.
                  </InviteMessage>
                )}
              </FormSection>
            </ModalContent>

            <ModalFooter>
              <SecondaryButton
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Saving...'
                  : guestId
                  ? 'Update Guest'
                  : 'Add Guest'}
              </PrimaryButton>
            </ModalFooter>
          </FormWrapper>
        </ModalContainer>
      </Backdrop>
    </EnhancedThemeProvider>,
    modalRoot
  );
};

export default GuestFormModal;
